import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { WACHTWOORD_MIN_LENGTE, keurWachtwoord } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Logo } from '../../design-system';
import { ApiError } from '../../lib/apiClient';
import { authApi } from './auth.api';
import { useAuth } from './useAuth';
import { WachtwoordMeter } from './WachtwoordMeter';

/**
 * Het scherm achter de herstellink uit de mail. Publiek bereikbaar: het token in
 * het pad is het enige bewijs. Zelfde opzet als `AccountAanmakenPage`, tot in de
 * breedte van de kaart — het is dezelfde handeling met een ander vertrekpunt.
 *
 * Geen naamveld: dit account bestaat al en hoe iemand heet staat er dus in. En
 * geen huidig wachtwoord: wie deze link heeft, heeft de mailbox, en dat is het
 * punt van herstellen.
 */
export function WachtwoordHerstellenPage() {
  const { token = '' } = useParams();
  const { zetNieuwWachtwoord } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string | null>(null);
  const [linkFout, setLinkFout] = useState('');
  const [laden, setLaden] = useState(true);

  const [wachtwoord, setWachtwoord] = useState('');
  const [herhaling, setHerhaling] = useState('');
  const [fouten, setFouten] = useState<string[]>([]);
  const [bezig, setBezig] = useState(false);

  // De link eerst laten keuren: verlopen of al gebruikt moet je meteen zien,
  // niet pas nadat je een wachtwoord hebt zitten bedenken.
  useEffect(() => {
    let actueel = true;
    authApi
      .checkHerstel(token)
      .then((r) => actueel && setEmail(r.email))
      .catch(
        (err) =>
          actueel &&
          setLinkFout(
            err instanceof ApiError ? err.message : 'Deze link kon niet worden gecontroleerd.',
          ),
      )
      .finally(() => actueel && setLaden(false));
    return () => {
      actueel = false;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFouten([]);

    // Dezelfde regels als de server, zodat je niet hoeft te wachten op een
    // rondje netwerk voor iets dat hier al te zien is.
    const lokaal = keurWachtwoord(wachtwoord, { email: email ?? '', naam: '' });
    if (wachtwoord !== herhaling) lokaal.push('De twee wachtwoorden zijn niet gelijk.');
    if (lokaal.length) return setFouten(lokaal);

    setBezig(true);
    try {
      await zetNieuwWachtwoord(token, { wachtwoord });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const details = (err.details as { fouten?: string[] } | undefined)?.fouten;
        setFouten(details?.length ? details : [err.message]);
      } else {
        setFouten(['Er ging iets mis. Probeer het opnieuw.']);
      }
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <span className="self-center">
          <Logo height={40} />
        </span>

        {laden ? (
          <Card>
            <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />
              Even kijken of deze link nog geldig is…
            </CardContent>
          </Card>
        ) : linkFout ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Deze link werkt niet meer.</CardTitle>
              <CardDescription>{linkFout}</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Een herstellink is een uur geldig en kan één keer gebruikt worden. Vraag op het
              inlogscherm een nieuwe aan.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Kies een nieuw wachtwoord.</CardTitle>
              <CardDescription>
                Voor <strong>{email}</strong>. Daarna ben je meteen ingelogd.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit}>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="wachtwoord">Nieuw wachtwoord</FieldLabel>
                    <Input
                      autoComplete="new-password"
                      id="wachtwoord"
                      onChange={(e) => setWachtwoord(e.target.value)}
                      required
                      type="password"
                      value={wachtwoord}
                    />
                    <FieldDescription>Minimaal {WACHTWOORD_MIN_LENGTE} tekens.</FieldDescription>
                    <WachtwoordMeter eigenWoorden={[email ?? '']} wachtwoord={wachtwoord} />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="herhaling">Wachtwoord nog een keer</FieldLabel>
                    <Input
                      autoComplete="new-password"
                      id="herhaling"
                      onChange={(e) => setHerhaling(e.target.value)}
                      required
                      type="password"
                      value={herhaling}
                    />
                  </Field>

                  {fouten.length > 0 && (
                    <Alert variant="destructive">
                      <AlertTitle>
                        {fouten.map((f) => (
                          <div key={f}>{f}</div>
                        ))}
                      </AlertTitle>
                    </Alert>
                  )}

                  <Button className="w-full" disabled={bezig} type="submit">
                    {bezig && <Spinner data-icon="inline-start" />}
                    {bezig ? 'Bezig…' : 'Wachtwoord opslaan'}
                  </Button>
                </FieldGroup>
              </form>
            </CardContent>
          </Card>
        )}

        <Link className="self-center text-sm underline-offset-4 hover:underline" to="/login">
          Terug naar inloggen
        </Link>
      </div>
    </div>
  );
}
